import Close from "@/assets/icons/Close";
import { useDebounce, useHasProperty, useViewport } from "@/hooks";
import {
  AutoComplete,
  Button,
  Divider,
  StyledTextField,
  Text,
} from "@/includes";
import { client } from "@/services/graphql/apolloConfig";
import { sendNotification } from "@/services/redux/actions";
import { arrayUtils } from "@/utils";
import { EdgeType } from "@/utils/types/requests";
import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  useTheme,
} from "@mui/material";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import ShortUniqueId from "short-unique-id";
import { DUPLICATE_EXPERIENCE, SEARCH_ORGANISATION } from "./requests";
import { IDuplicateOnOtherSiteDialog } from "./types";
import { FLAGS_ENUM } from "@/utils/enums/flags";

const DuplicateInOtherSiteDialog = ({
  open,
  handleClose,
  experienceId,
  experienceName,
  updateExperiences,
}: IDuplicateOnOtherSiteDialog) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isMobile } = useViewport();
  const dispatch = useDispatch();
  const uid = new ShortUniqueId();
  const { getValueOfFlag } = useHasProperty();

  const [selectedValue, setSelectedValue] = useState<Dic<any>>({});
  const [inputValue, setInputValue] = useState<string>("");
  const [search, setSearch] = useState(inputValue);

  const { data, loading } = useQuery(SEARCH_ORGANISATION, {
    variables: {
      search: selectedValue?.name == search ? "" : search,
      first: 1000,
    },
  });

  const options =
    data?.companies?.edges
      ?.map((edge: EdgeType) => ({
        name: edge?.node?.name,
        id: edge?.node?.id,
        sites: edge?.node?.sites?.edges?.map((subItem: Dic<Dic<string>>) => ({
          id: subItem?.node?.id,
          name: subItem?.node?.name,
          isUnderMaintenance: subItem?.node?.isUnderMaintenance,
        })),
      }))
      ?.map((c: any) => {
        return c?.sites?.map((s: any) => ({
          ...s,
          organisationId: c?.id,
          organisationName: c?.name,
        }));
      })
      ?.flat() ?? [];

  useEffect(() => {
    if (isEmpty(selectedValue) && !isEmpty(options))
      setSelectedValue(options[0]);
  }, [options]);

  useDebounce(
    () => {
      setSearch(inputValue);
    },
    [inputValue],
    600
  );

  const onClose = () => {
    setSelectedValue({});
    handleClose();
  };

  const handleDuplicate = () => {
    client
      .mutate({
        mutation: DUPLICATE_EXPERIENCE,
        variables: {
          experienceId,
          destinationSiteId: selectedValue?.id,
        },
      })
      .then(() => {
        updateExperiences();
        dispatch(
          sendNotification(
            "duplicate_experience",
            t("duplicate"),
            t("duplicate_experience_success"),
            "success"
          )
        );
        onClose();
      })
      .catch((err: any) => {
        dispatch(
          sendNotification(
            "duplicate_experience",
            t("duplicate"),
            `${t("error")}: ${err.message}`,
            "error"
          )
        );
      });
  };

  const sites = useMemo(() => arrayUtils.uniqueArray([...options]), [options]);

  const optionsSortedNySiteName = useMemo(
    () =>
      options
        ?.filter((site: Dic<any>) => !isEmpty(site?.name))
        .sort(arrayUtils.dynamicSort("name")),
    [options]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "min(100vw, 550px)",
          maxWidth: "min(100vw, 550px)",
        },
      }}
    >
      <Stack spacing={2} sx={{ padding: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="start"
        >
          <Text variant="h3">
            {t("duplicate")} {experienceName}
          </Text>
          <IconButton onClick={onClose}>
            <Close color={theme.customPalette.grey.default} />
          </IconButton>
        </Stack>
        <DialogContent sx={{ padding: 0 }}>
          <Text customStyle={{ mb: 2 }}>
            {t("select_site_to_duplicate_experience")}
          </Text>
          <AutoComplete
            filterOptions={(x) => x}
            autoHighlight
            onChange={(e: React.SyntheticEvent, value: any) => {
              setSelectedValue(value);
            }}
            groupBy={(option: any) => option?.organisationId}
            value={selectedValue}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
              setInputValue(newInputValue);
            }}
            noOptionsText=""
            loadingText=""
            loading={loading}
            disableClearable
            id="site-selector"
            options={optionsSortedNySiteName}
            getOptionLabel={(option: any) => option?.name ?? ""}
            renderInput={(params: any) => (
              <StyledTextField
                {...params}
                label=""
                sx={{
                  fontSize: 14,
                  "& .MuiInputBase-root.MuiOutlinedInput-root": {
                    py: 0,
                    "& input": {
                      py: 0.75,
                    },
                  },
                }}
                value={inputValue}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props: any, option: any, { selected }: any) => {
              return (
                <li
                  key={uid()}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  style={{
                    cursor: "pointer",
                    "&:hover": {
                      background: theme.customPalette.others.layer_2,
                    },
                  }}
                >
                  <Text
                    variant="body1"
                    customStyle={{
                      paddingLeft: 1,
                    }}
                  >
                    {option.name}
                  </Text>
                </li>
              );
            }}
            renderGroup={(params) => {
              const organisation = sites?.find(
                (c: any) => c?.organisationId === params?.group
              );
              return (
                <Box key={uid()} p={1}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      padding: "2px 0",
                    }}
                  >
                    <Box
                      sx={{ width: 16, height: 16 }}
                      component="img"
                      alt=""
                      src={`/${theme.palette.mode}/others/account.svg`}
                    />
                    <Text variant="body2">
                      {organisation?.organisationName}
                    </Text>
                  </Stack>
                  <Box>{params.children}</Box>
                  <Divider />
                </Box>
              );
            }}
          />
        </DialogContent>
        <DialogActions sx={{ gap: 3, p: 0 }}>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: "100%" }}
            spacing={1}
          >
            <Button
              btnVariant="outline"
              id="cancelBtn"
              onClick={onClose}
              customStyle={{ width: isMobile ? "100%" : "50%" }}
            >
              {t("cancel")}
            </Button>
            <Button
              customStyle={{ width: isMobile ? "100%" : "50%" }}
              onClick={handleDuplicate}
              id="duplicate"
            >
              {t("duplicate")}
            </Button>
          </Stack>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};

export default DuplicateInOtherSiteDialog;
